import React, { useEffect, useState } from "react";
import { Badge } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getAttendanceList } from "action/attendanceModule";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import DetailsHeader from "components/CommonComponents/DetailsHeader";


const AttendanceRightPanel = ({
  isOpen,
  onClose,
  selectedTrainee,
  courseID,
  batchId,
  courseName,
  batchName,
}) => {
  const dispatch = useDispatch();

  const [attendanceState, setAttendanceState] = useState({
    isLoading: false,
    error: "",
  });

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pageNum, setPageNum] = useState(1);

  const attendanceListRes = useSelector(
    (state) => state.attendanceModule.attendanceList,
  );

  useEffect(() => {
    if (!isOpen) {
      setPageNum(1);
    }
  }, [isOpen]);


  // Attendance API
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!isOpen || !selectedTrainee) {
        return;
      }

      setAttendanceState({
        isLoading: true,
        error: "",
      });

      const traineeId =
        selectedTrainee.traineeId ||
        selectedTrainee.traineeID ||
        selectedTrainee.enrollmentID;


      const cIdDecoded =
        courseID !== null && courseID !== undefined
          ? typeof courseID === "number"
            ? courseID
            : urlDecodedId(courseID)
          : null;
      const bIdDecoded =
        batchId !== null && batchId !== undefined
          ? typeof batchId === "number"
            ? batchId
            : urlDecodedId(batchId)
          : null;

      await getAttendanceList(dispatch, cIdDecoded, bIdDecoded, traineeId, pageNum);
    };

    fetchAttendance();
  }, [isOpen, selectedTrainee, courseID, batchId, pageNum, dispatch]);

  // Attendance API response
  useEffect(() => {
    if (attendanceListRes?.code === 200) {
      setAttendanceState({
        isLoading: false,
        error: "",
      });
    } else if (attendanceListRes?.code || attendanceListRes?.message) {
      setAttendanceState({
        isLoading: false,
        error: attendanceListRes?.message || "Failed to load attendance list.",
      });
    }
  }, [attendanceListRes]);

  // Attendance table data
  const attendanceTableData = attendanceListRes?.data?.attendanceDetails?.map(
    (item, index) => ({
      srNo: index + 1,
      scheduleDate: item.sessionDate || "-",
      startTime: item.sessionStartTime || "-",
      endTime: item.sessionEndTime || "-",
      attendance: item.attendanceStatus || "-",
    }),
  );

  // Trainee details
  const traineeDetails = attendanceListRes?.data?.traineeDetails;

  const currentCourseName = traineeDetails?.courseName || courseName || "";

  const currentBatchName = traineeDetails?.batchName || batchName || "";

  // Trainee details chips
  const traineeDetailsChips = [
    {
      id: `course-chip-${selectedTrainee?.enrollmentID || "0"}`,
      name: "Course",
      value:
        currentCourseName.length > 50
          ? `${currentCourseName.substring(0, 50)}...`
          : currentCourseName || "-",
      icon: "fa fa-book",
      tooltip: currentCourseName.length > 50 ? currentCourseName : undefined,
    },
    {
      id: `batch-chip-${selectedTrainee?.enrollmentID || "0"}`,
      name: "Batch",
      value:
        currentBatchName.length > 50
          ? `${currentBatchName.substring(0, 50)}...`
          : currentBatchName || "-",
      icon: "fa fa-users",
      tooltip: currentBatchName.length > 50 ? currentBatchName : undefined,
    },
  ];

  // Attendance table columns
  const attendanceColumns = [
    {
      Header: "Sr No",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 70,
      maxWidth: 70,
    },
    {
      Header: "Session Date",
      accessor: "scheduleDate",
      minWidth: 120,
      maxWidth: 120,
    },
    {
      Header: "Session Start Time",
      accessor: "startTime",
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Session End Time",
      accessor: "endTime",
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Attendance",
      accessor: "attendance",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ value }) => (
        <Badge
          color={
            value === "PRESENT"
              ? "success"
              : value === "ABSENT"
                ? "danger"
                : "warning"
          }
          pill
        >
          {value}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <RightPanelCard
        isOpen={isOpen}
        onClose={onClose}
        isSmall={false}
        fullHeight={true}
        enableScroll={false}
      >
        <div className="p-3">
          <DetailsHeader
            isLoading={attendanceState.isLoading}
            title={traineeDetails?.traineeName || selectedTrainee?.traineeName}
            profileImage={traineeDetails?.profileImagePath}
            onProfileClick={
              traineeDetails?.profileImagePath
                ? () => setShowProfileModal(true)
                : undefined
            }
            chips={traineeDetailsChips}
            showProfile={true}
            showEdit={false}
            onBack={onClose}
          />

          <div className="mt-4 mb-3 mx-1 d-flex justify-content-between align-items-center">
            <h3 className="mb-0">Attendance</h3>

            <span className="total-records">
              Total Records : {attendanceListRes?.data?.totalRecords || 0}
            </span>
          </div>

          <div className="table-responsive mb-5">
            {attendanceState.isLoading ? (
              <div className="loaderContainer-sm">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : attendanceState.error ? (
              <div className="loaderContainer-sm error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation" />
                </div>

                <span>{attendanceState.error}</span>
              </div>
            ) : !attendanceTableData || attendanceTableData.length === 0 ? (
              <div className="loaderContainer-sm empty-style">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info" />
                </div>

                <span>No records found.</span>
              </div>
            ) : (
              <CommonTable
                columns={attendanceColumns}
                data={attendanceTableData}
                defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                showPaginationBottom={true}
                activePage={pageNum}
                setPageNum={setPageNum}
                totalPages={Math.ceil(
                  attendanceListRes?.data?.totalFilteredRecords /
                  attendanceListRes?.data?.noOfRecordsPerPage ||
                  DEFAULT_RECORDS_PER_PAGE,
                )}
              />
            )}
          </div>
        </div>
      </RightPanelCard>

      {/* Profile Image Modal */}
      {showProfileModal && traineeDetails?.profileImagePath && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowProfileModal(false)}
        >
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={traineeDetails.profileImagePath} alt="profile-large" />
          </div>
        </div>
      )}
    </>
  );
};

export default AttendanceRightPanel;
